import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CartDrawer from "@/components/feature/CartDrawer";
import ProductRail from "@/pages/home/components/ProductRail";
import InstagramShelf from "@/pages/home/components/InstagramShelf";
import { categories as mockCategories, products as mockProducts, type Product } from "@/mocks/products";
import { fetchBestSellers, fetchCatalogCached, fetchStoreCategories, isStoreEnabled, type StoreCategory } from "@/services/store-api";
import { dailyPicks, newestProducts, storeDay } from "@/utils/collections";
import "./landing.css";

const RAIL_SIZE = 12;
const SLIDE_MS = 6000;
const base = import.meta.env.BASE_URL;
const photo = (name: string) => `${base}editorial/${name}.webp`;

// Banners da coleção: foto do catálogo com o texto na própria página, para ficar nítido em qualquer tela.
const slides = [
  { id: "primavera-verao", image: photo("banner-rosa"), alt: "Modelo sentada diante de uma porta de madeira, vestindo conjunto rosa", kicker: "Nova coleção", title: ["Primavera-Verão", "2026/2027"], text: "Tons pastel e caimento leve para os dias de sol.", cta: "Ver coleção", href: "/produtos?colecao=novidades" },
  { id: "conjuntos", image: photo("banner-azul"), alt: "Modelo entre palmeiras, vestindo conjunto azul", kicker: "Novidades", title: ["Alfaiataria", "para o verão"], text: "Peças leves para usar do dia à noite.", cta: "Ver novidades", href: "/produtos?colecao=novidades", reverse: true },
];

function SeasonBanner({ slide, priority }: { slide: (typeof slides)[number]; priority: boolean }) {
  return (
    <div className={`bz-season ${slide.reverse ? "is-reverse" : ""}`}>
      <div className="bz-season-copy">
        <img className="bz-season-logo" src={`${base}bozzetti-logo.webp`} alt="" />
        <p>{slide.kicker}</p>
        <h2>{slide.title[0]}<br />{slide.title[1]}</h2>
        <span>{slide.text}</span>
        <Link to={slide.href}>{slide.cta} <ArrowRight size={14} aria-hidden="true" /></Link>
      </div>
      <img className="bz-season-photo" src={slide.image} alt={slide.alt} fetchPriority={priority ? "high" : undefined} />
    </div>
  );
}

// A página inicial é a vitrine; o catálogo completo, com filtros, fica em /produtos.
// Endereços antigos como /?categoria=ID ou /?colecao=promocoes seguem funcionando.
export default function Landing() {
  const [params] = useSearchParams();
  const location = useLocation();
  if (params.has("categoria") || params.has("colecao") || params.has("q")) {
    return <Navigate to={`/produtos${location.search}`} replace />;
  }
  return <LandingPage />;
}

function Carousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const step = useCallback((direction: number) => setActive((index) => (index + direction + slides.length) % slides.length), []);
  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => step(1), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [paused, step]);
  return (
    <section className="bz-carousel" aria-roledescription="carrossel" aria-label="Destaques da loja" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {slides.map((slide, index) => (
        <div key={slide.id} className={`bz-carousel-slide ${index === active ? "is-on" : ""}`} aria-hidden={index !== active}>
          <SeasonBanner slide={slide} priority={index === 0} />
        </div>
      ))}
      <button type="button" className="bz-carousel-arrow prev" aria-label="Foto anterior" onClick={() => step(-1)}><ChevronLeft size={22} aria-hidden="true" /></button>
      <button type="button" className="bz-carousel-arrow next" aria-label="Próxima foto" onClick={() => step(1)}><ChevronRight size={22} aria-hidden="true" /></button>
      <div className="bz-dots">
        {slides.map((slide, index) => <button key={slide.id} type="button" aria-label={`Foto ${index + 1}`} aria-current={index === active} onClick={() => setActive(index)} />)}
      </div>
    </section>
  );
}

function Tile({ to, image, label, action, className = "" }: { to: string; image: string; label: string; action: string; className?: string }) {
  return (
    <Link to={to} className={`bz-tile ${className}`}>
      <img src={image} alt="" loading="lazy" decoding="async" />
      <span className="bz-tile-label"><b>{label}</b><span>{action} →</span></span>
    </Link>
  );
}

function LandingPage() {
  const live = isStoreEnabled();
  const [products, setProducts] = useState<Product[]>(live ? [] : mockProducts);
  const [categories, setCategories] = useState<StoreCategory[]>(live ? [] : mockCategories.map((c) => ({ id: c.id, label: c.label })));
  const [bestSellers, setBestSellers] = useState<Product[]>([]);

  const load = useCallback((attempt = 0) => {
    // Cada bloco carrega por conta própria: se um falhar, a página não fica sem os outros.
    fetchCatalogCached()
      .then((items) => setProducts(items))
      .catch(() => { if (attempt === 0) window.setTimeout(() => load(1), 1500); });
    fetchBestSellers(RAIL_SIZE).then(setBestSellers).catch(() => { /* usa a seleção do dia */ });
    fetchStoreCategories().then(setCategories).catch(() => { /* mosaico sem categorias */ });
  }, []);
  useEffect(() => { if (live) load(); }, [live, load]);


  const day = storeDay();
  const newest = useMemo(() => newestProducts(products, RAIL_SIZE), [products]);
  const selection = useMemo(() => dailyPicks(products, RAIL_SIZE, day, "recomendados"), [products, day]);
  const sets = useMemo(() => {
    const first = categories[0];
    return first ? { id: first.id, label: first.label, items: products.filter((product) => (product.categoryIds ?? []).includes(Number(first.id))) } : null;
  }, [categories, products]);
  const categoryTiles = useMemo(() => {
    const newestFirst = [...products].sort((a, b) => b.id - a.id);
    return categories
      .map((category) => ({
        id: category.id,
        label: category.label,
        image: newestFirst.find((product) => (product.categoryIds ?? []).includes(Number(category.id)))?.image,
      }))
      .filter((category) => category.image)
      .slice(0, 4);
  }, [categories, products]);

  return (
    <div className="bz-page bz-home">
      <Header />
      <main>
        <h1 className="sr-only">Bozzetti Store, alfaiataria feminina</h1>
        <Carousel />

        <nav className="bz-mosaic" aria-label="Categorias">
          {categoryTiles.map((category) => (
            <Tile key={category.id} to={`/produtos?categoria=${encodeURIComponent(category.id)}`} image={category.image as string} label={category.label} action="Ver peças" />
          ))}
          <Tile to="/produtos" image={photo("mosaico-nude")} label="Alfaiataria" action="Ver coleção" className="bz-tile-large" />
        </nav>

        <ProductRail kicker="Primavera-Verão 2026/2027" title="Lançamentos" products={newest} moreHref="/produtos?colecao=novidades" />
        {sets && <ProductRail kicker="Coleção" title={sets.label} products={sets.items} moreHref={`/produtos?categoria=${encodeURIComponent(sets.id)}`} />}
        <ProductRail kicker="Escolhidas para você" title="Seleção da semana" products={selection} moreHref="/produtos" />

        <div className="bz-duo">
          <Tile to="/produtos" image={photo("atelier-branco")} label="Alfaiataria" action="Explorar peças" />
          <Tile to="/produtos?colecao=novidades" image={photo("bloco-preto")} label="Novidades" action="Ver novidades" />
        </div>

        {bestSellers.length > 0 && <ProductRail kicker="As preferidas" title="Mais vendidos" products={bestSellers} moreHref="/produtos?colecao=vendidos" />}

        <div className="bz-trio">
          {[["trio-blazer", "Blazers"], ["trio-camisa", "Camisas"], ["trio-shorts", "Shorts"]].map(([image, label]) => (
            <Link key={image} to="/produtos" className="bz-tile">
              <img src={photo(image)} alt="" loading="lazy" decoding="async" />
              <span className="bz-tile-button">{label} →</span>
            </Link>
          ))}
        </div>

        <InstagramShelf />

        <section className="bz-partner" aria-labelledby="bz-partner-title">
          <div>
            <p>Programa de afiliados</p>
            <h2 id="bz-partner-title">Indique a Bozzetti e ganhe comissão</h2>
            <span>Compartilhe seu link exclusivo e acompanhe suas indicações em um painel só seu.</span>
            <Link to="/afiliados">Conhecer o programa <ArrowRight size={14} aria-hidden="true" /></Link>
          </div>
          <ol>
            <li><b>1</b>Solicite sua participação</li>
            <li><b>2</b>Compartilhe seu link</li>
            <li><b>3</b>Acompanhe suas comissões</li>
          </ol>
        </section>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
